import { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { CATEGORIES, PRIORITIES, categoryColor, categoryLabel, chatTypeClasses, formatRelative, formatDateTime, priorityClasses } from "../lib/constants";
import { Check, X, ShieldCheck, Sparkles, Trash2, Calendar, CheckSquare, Square, PenLine, MessageSquare, Users, User, ChevronDown, ChevronRight, Loader2, Search, Layers } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../components/Confirm";
import DeadlinePicker from "../components/DeadlinePicker";
import { toLocalInputValue } from "../lib/utils";

export default function ReviewQueue() {
  const confirm = useConfirm();
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  // "none" | "chat" | "sender" — remembered per browser.
  const [groupBy, setGroupByState] = useState(() => {
    try { return localStorage.getItem("sw_review_group_by") || "none"; } catch { return "none"; }
  });
  const setGroupBy = (v) => {
    setGroupByState(v);
    setSelected(new Set());
    try { localStorage.setItem("sw_review_group_by", v); } catch { /* private mode */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/tasks", { params: { in_review: true, status: "pending" } });
      setAllItems(r.data.tasks || []);
      setSelected(new Set()); // Clear selection on load
    } catch {
      toast.error("Failed to load items");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filteredItems = allItems.filter((t) => {
    let byCategory = true;
    if (tab === "opportunities") byCategory = t.category === "opportunity";
    else if (tab === "enquiries") byCategory = t.category === "new_enquiry";
    else if (tab === "self_sent") byCategory = t.is_self_sent === true;

    let byPriority = true;
    if (priorityFilter !== "all") byPriority = t.priority === priorityFilter;

    return byCategory && byPriority;
  });

  const sortedItems = [...filteredItems].sort((a, b) => {
    if (sortBy === "newest") return new Date(b.created_at) - new Date(a.created_at);
    if (sortBy === "oldest") return new Date(a.created_at) - new Date(b.created_at);
    if (sortBy === "confidence") return (a.confidence || 0) - (b.confidence || 0);
    return 0;
  });

  const toggleSelect = (id) => {
    const newSelected = new Set(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelected(newSelected);
  };

  const selectAll = () => {
    if (selected.size === sortedItems.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(sortedItems.map((t) => t.id)));
    }
  };

  const clearSelection = () => {
    setSelected(new Set());
  };

  // Single-card actions only hit the API and report success. The card plays its
  // exit animation in parallel and calls removeItem once both are done, so the
  // row doesn't vanish mid-animation.
  const reviewTask = async (id, action, edits = {}) => {
    try {
      await api.post(`/tasks/${id}/review`, { action, ...edits });
      return true;
    } catch {
      toast.error("Action failed");
      return false;
    }
  };

  const deleteTask = async (id) => {
    try {
      await api.delete(`/tasks/${id}`);
      return true;
    } catch {
      toast.error("Delete failed");
      return false;
    }
  };

  const removeItem = (id) => {
    setAllItems((cur) => cur.filter((t) => t.id !== id));
    setSelected((cur) => {
      if (!cur.has(id)) return cur;
      const next = new Set(cur);
      next.delete(id);
      return next;
    });
  };

  const decideMultiple = async (ids, action, edits = {}) => {
    try {
      await Promise.all(ids.map((id) => api.post(`/tasks/${id}/review`, { action, ...edits })));
      setAllItems((cur) => cur.filter((t) => !ids.includes(t.id)));
      toast.success(action === "reject" ? `${ids.length} skipped` : `${ids.length} approved`);
    } catch {
      toast.error("Some actions failed");
    }
  };

  const bulkApprove = async () => {
    if (selected.size === 0) return;
    const count = selected.size;
    const confirmApprove = await confirm({
      title: `Approve ${count} item${count > 1 ? "s" : ""}?`,
      message: "They'll all be set with a deadline of tomorrow at 9:00 AM.",
      confirmLabel: "Approve",
    });
    if (!confirmApprove) return;

    setBulkLoading(true);
    try {
      // Calculate tomorrow at 09:00
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      const tomorrowISO = tomorrow.toISOString();

      // Approve all selected items
      const promises = Array.from(selected).map((id) =>
        api.post(`/tasks/${id}/review`, { action: "approve", deadline: tomorrowISO })
      );

      await Promise.all(promises);

      // Remove approved items from allItems
      setAllItems((cur) => cur.filter((t) => !selected.has(t.id)));
      setSelected(new Set());
      toast.success(`${count} task(s) approved with deadline tomorrow`);
    } catch (err) {
      console.error("Bulk approve error:", err);
      toast.error("Some items failed to approve");
    } finally {
      setBulkLoading(false);
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const count = selected.size;
    const confirmDelete = await confirm({
      title: `Delete ${count} item${count > 1 ? "s" : ""}?`,
      message: "These review-queue items will be removed permanently.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmDelete) return;

    setBulkLoading(true);
    try {
      // Delete all selected items
      const promises = Array.from(selected).map((id) =>
        api.delete(`/tasks/${id}`)
      );

      await Promise.all(promises);

      // Remove deleted items from allItems
      setAllItems((cur) => cur.filter((t) => !selected.has(t.id)));
      setSelected(new Set());
      toast.success(`${count} item(s) deleted`);
    } catch (err) {
      console.error("Bulk delete error:", err);
      toast.error("Some items failed to delete");
    } finally {
      setBulkLoading(false);
    }
  };

  const tabCounts = {
    all: allItems.length,
    opportunities: allItems.filter((t) => t.category === "opportunity").length,
    enquiries: allItems.filter((t) => t.category === "new_enquiry").length,
    self_sent: allItems.filter((t) => t.is_self_sent === true).length,
  };

  // Group self-sent tasks by chat_id so messages to the same recipient appear as a thread.
  const selfSentGroups = useMemo(() => {
    const selfSentItems = sortedItems.filter((t) => t.is_self_sent === true);
    const groupMap = {};
    selfSentItems.forEach((task) => {
      const key = task.chat_id || "__unknown__";
      if (!groupMap[key]) {
        groupMap[key] = {
          key,
          chat_id: task.chat_id,
          chat_name: task.chat_name,
          chat_type: task.chat_type,
          tasks: [],
        };
      }
      groupMap[key].tasks.push(task);
    });
    return Object.values(groupMap);
  }, [sortedItems]);

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="review-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-[32px] font-extrabold tracking-tight text-slate-900 leading-tight">Review Queue</h1>
          <p className="text-slate-500 text-[14px] mt-1">
            AI has processed conversations. Review and approve the suggested actions.
          </p>
        </div>
        <div className="flex flex-wrap bg-slate-100 rounded-xl p-1 gap-0.5">
          {[
            { key: "all", label: "All Pending" },
            { key: "opportunities", label: "Opportunities" },
            { key: "enquiries", label: "Enquiries" },
            { key: "self_sent", label: "Self Sent" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => { setTab(key); setSelected(new Set()); }}
              className={`px-4 py-2 rounded-lg text-[13px] font-semibold transition flex items-center gap-1.5 ${
                tab === key
                  ? key === "self_sent"
                    ? "bg-white text-violet-600 shadow-sm"
                    : "bg-white text-blue-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-900"
              }`}
            >
              {key === "self_sent" && <PenLine size={13} />}
              {label} ({tabCounts[key]})
            </button>
          ))}
        </div>
      </div>

      {/* Group by */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="review-group-by">
        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500 mr-1 inline-flex items-center gap-1.5"><Layers size={13} /> Group by:</span>
        {[["none", "Nothing"], ["chat", "Chat (group / person)"], ["sender", "Sender"]].map(([k, label]) => (
          <button key={k} onClick={() => setGroupBy(k)}
            className={`px-4 py-1.5 rounded-full text-[12px] font-semibold transition border ${groupBy === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400 bg-white"}`}
            data-testid={`group-by-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {groupBy !== "none" ? (
        <GroupedReview by={groupBy} onReview={reviewTask} onDeleteTask={deleteTask} onChanged={load} />
      ) : (<>
      {/* Filter Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-4 border-b border-slate-200">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500 mr-1">Priority:</span>
          {["all", "low", "medium", "high", "urgent"].map((p) => (
            <button
              key={p}
              onClick={() => { setPriorityFilter(p); setSelected(new Set()); }}
              className={`px-4 py-1.5 rounded-full text-[12px] font-semibold transition border ${
                priorityFilter === p
                  ? "bg-blue-600 text-white border-blue-600"
                  : "border-slate-200 text-slate-600 hover:border-blue-400 bg-white"
              }`}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}

          <div className="text-[12px] text-slate-500 uppercase tracking-wider">Sort:</div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="h-8 px-2 border border-slate-200 rounded-sm bg-white text-[12px] focus:border-slate-900 outline-none"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="confidence">Confidence (low first)</option>
          </select>
        </div>

        {/* Select All */}
        {sortedItems.length > 0 && (
          <button
            onClick={selectAll}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-slate-600 hover:text-slate-900 transition"
          >
            {selected.size === sortedItems.length ? (
              <>
                <CheckSquare size={14} className="text-blue-600" />
                <span>All selected ({selected.size})</span>
              </>
            ) : selected.size > 0 ? (
              <>
                <CheckSquare size={14} className="text-blue-600" />
                <span>{selected.size} selected</span>
              </>
            ) : (
              <>
                <Square size={14} />
                <span>Select all</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Bulk Action Bar */}
      {selected.size > 0 && (
        <div className="bg-[#0F172A] text-white rounded-xl px-5 py-3 mb-4 flex items-center gap-4 sw-fade-up" data-testid="bulk-action-bar">
          <span className="text-[13px] font-semibold">{selected.size} selected</span>
          <div className="flex gap-2 ml-auto">
            <button
              onClick={bulkApprove}
              disabled={bulkLoading}
              className="h-9 px-4 inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[13px] font-semibold transition active:scale-95 disabled:opacity-50"
              data-testid="bulk-approve-btn"
            >
              <Check size={14} />
              Approve All (Tomorrow)
            </button>
            <button
              onClick={bulkDelete}
              disabled={bulkLoading}
              className="h-9 px-4 inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-[13px] font-semibold transition active:scale-95 disabled:opacity-50"
              data-testid="bulk-delete-btn"
            >
              <Trash2 size={14} />
              Delete All
            </button>
            <button
              onClick={clearSelection}
              disabled={bulkLoading}
              className="h-9 px-4 inline-flex items-center gap-1.5 border border-white/20 hover:border-white/40 text-white rounded-lg text-[13px] font-semibold transition disabled:opacity-50"
              data-testid="clear-selection-btn"
            >
              <X size={14} />
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2.5" data-testid="review-list">
        {loading && (
          <div className="bg-white border border-slate-200 rounded-sm p-10 text-center text-slate-500 text-[13px]">Loading…</div>
        )}
        {!loading && sortedItems.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="review-empty">
            <span className="material-symbols-outlined text-slate-300 text-5xl" style={{ fontVariationSettings: "'FILL' 1" }}>fact_check</span>
            <div className="text-xl font-bold text-slate-800 mt-4">Inbox zero</div>
            <p className="text-slate-500 text-[14px] mt-1">No items waiting for human review.</p>
          </div>
        )}
        {tab === "self_sent" && !loading ? (
          selfSentGroups.map((group) => (
            <SelfSentConversationGroup
              key={group.key}
              group={group}
              onReview={reviewTask}
              onDecideMultiple={decideMultiple}
              onDeleteTask={deleteTask}
              onRemoved={removeItem}
              selected={selected}
              onToggleSelect={toggleSelect}
            />
          ))
        ) : (
          sortedItems.map((t, i) => (
            <ReviewCard
              key={t.id}
              task={t}
              onReview={reviewTask}
              onDeleteTask={deleteTask}
              onRemoved={removeItem}
              isOpportunityOrEnquiry={t.category === "opportunity" || t.category === "new_enquiry"}
              isSelfSent={t.is_self_sent === true}
              isSelected={selected.has(t.id)}
              onToggleSelect={() => toggleSelect(t.id)}
              delay={i * 30}
            />
          ))
        )}
      </div>
      </>)}
    </div>
  );
}

// ── Grouped view: one card per chat or sender, decided in one go ─────────────

function GroupedReview({ by, onReview, onDeleteTask, onChanged }) {
  const confirm = useConfirm();
  const [data, setData] = useState(null);       // {total, groups}
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("count");    // "count" | "newest"
  const [open, setOpen] = useState({});         // key -> true
  const [busy, setBusy] = useState({});         // key -> progress text

  const load = async () => {
    try {
      const r = await api.get("/tasks/review-groups", { params: { by } });
      setData(r.data);
    } catch {
      toast.error("Couldn't load groups");
      setData({ total: 0, groups: [] });
    }
  };
  useEffect(() => { setData(null); setOpen({}); load(); }, [by]); // eslint-disable-line react-hooks/exhaustive-deps

  const bump = (key, delta) => setData((d) => d && ({
    ...d,
    total: Math.max(0, d.total + delta),
    groups: d.groups.map((g) => (g.key === key ? { ...g, count: g.count + delta } : g)).filter((g) => g.count > 0),
  }));

  const decideAll = async (g, action) => {
    const verb = action === "approve" ? "Approve" : "Reject";
    const ok = await confirm({
      title: `${verb} all ${g.count} from "${g.label}"?`,
      message: action === "approve"
        ? "They become tasks with automatic deadlines in free slots. No calendar invites are emailed for bulk approvals."
        : "They're removed from the review queue (you can still find them under Rejected in Tasks).",
      confirmLabel: `${verb} ${g.count}`,
      tone: action === "approve" ? undefined : "danger",
    });
    if (!ok) return;
    let total = 0;
    setBusy((b) => ({ ...b, [g.key]: action === "approve" ? "Approving…" : "Rejecting…" }));
    try {
      for (let guard = 0; guard < 50; guard++) {
        const r = await api.post("/tasks/review-bulk", { action, group_by: by, group_key: g.key });
        total += r.data.done;
        if (action === "approve") setBusy((b) => ({ ...b, [g.key]: `Approving… ${total} of ${g.count}` }));
        if (!r.data.remaining || !r.data.done) break;
      }
      toast.success(`${action === "approve" ? "Approved" : "Rejected"} ${total} from ${g.label}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Some items failed — refreshed the list");
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[g.key]; return n; });
      await load();
      onChanged?.();
    }
  };

  if (!data) {
    return <div className="bg-white border border-slate-200 rounded-xl p-10 grid place-items-center text-slate-400"><Loader2 className="animate-spin" /></div>;
  }
  const q = search.trim().toLowerCase();
  const groups = data.groups
    .filter((g) => !q || g.label.toLowerCase().includes(q) || (g.sample_titles || []).some((t) => t.toLowerCase().includes(q)))
    .sort((a, b) => (sort === "newest" ? new Date(b.newest) - new Date(a.newest) : b.count - a.count));

  return (
    <div data-testid="review-grouped">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-4 border-b border-slate-200">
        <div className="text-[13px] text-slate-600">
          <b className="text-slate-900">{data.total}</b> waiting in <b className="text-slate-900">{data.groups.length}</b> {by === "chat" ? "chats" : "senders"} — whole queue, newest activity shown per group.
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={by === "chat" ? "Find a chat…" : "Find a sender…"}
              className="h-8 w-52 pl-8 pr-2 border border-slate-200 rounded-lg text-[12.5px] outline-none focus:border-blue-600 bg-white" />
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-8 px-2 border border-slate-200 rounded-lg bg-white text-[12px]">
            <option value="count">Most items first</option>
            <option value="newest">Most recent first</option>
          </select>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500 text-[14px]">{data.total ? "No groups match." : "Inbox zero — nothing waiting for review."}</div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <GroupCard key={g.key || "__none__"} g={g} by={by} open={!!open[g.key]} busy={busy[g.key]}
              onToggle={() => setOpen((o) => ({ ...o, [g.key]: !o[g.key] }))}
              onDecideAll={(action) => decideAll(g, action)}
              onReview={onReview} onDeleteTask={onDeleteTask}
              onItemRemoved={() => { bump(g.key, -1); onChanged?.(); }} />
          ))}
        </div>
      )}
    </div>
  );
}

function GroupCard({ g, by, open, busy, onToggle, onDecideAll, onReview, onDeleteTask, onItemRemoved }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!open || items) return;
    api.get("/tasks", { params: { in_review: true, status: "pending", group_by: by, group_key: g.key } })
      .then((r) => setItems(r.data.tasks || []))
      .catch(() => { toast.error("Couldn't load this group"); setItems([]); });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const Icon = g.kind === "group" ? Users : g.kind === "self" ? PenLine : User;
  const tone = g.kind === "group" ? "bg-purple-50 border-purple-200 text-purple-600"
    : g.kind === "self" ? "bg-violet-50 border-violet-200 text-violet-600" : "bg-blue-50 border-blue-200 text-blue-600";
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="review-group">
      <div className="px-4 py-3 flex items-center gap-3">
        <button onClick={onToggle} className="flex items-center gap-3 flex-1 min-w-0 text-left">
          {open ? <ChevronDown size={16} className="text-slate-400 shrink-0" /> : <ChevronRight size={16} className="text-slate-400 shrink-0" />}
          <div className={`w-9 h-9 grid place-items-center rounded-lg border shrink-0 ${tone}`}><Icon size={16} /></div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[14px] font-semibold text-slate-900 truncate">{g.label}</span>
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-900 text-white shrink-0">{g.count}</span>
              <span className="text-[11px] text-slate-400 shrink-0 hidden sm:inline">{g.kind === "group" ? "Group" : g.kind === "self" ? "Your messages" : by === "chat" ? "Person" : "Sender"}</span>
            </div>
            <div className="text-[12px] text-slate-500 truncate mt-0.5">
              Latest {formatRelative(g.newest)}
              {(g.top_categories || []).length > 0 && <> · {g.top_categories.map((c) => categoryLabel(c)).join(", ")}</>}
              {g.chats && g.chats.length > 0 && <> · in {g.chats.slice(0, 2).join(", ")}{g.chats.length > 2 ? ` +${g.chats.length - 2}` : ""}</>}
            </div>
            {!open && (g.sample_titles || []).length > 0 && (
              <div className="text-[12px] text-slate-400 truncate mt-0.5">“{g.sample_titles.slice(0, 2).join("” · “")}”</div>
            )}
          </div>
        </button>
        {busy ? (
          <span className="text-[12px] text-slate-500 inline-flex items-center gap-1.5 shrink-0"><Loader2 size={13} className="animate-spin" /> {busy}</span>
        ) : (
          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={() => onDecideAll("approve")} className="h-8 px-3 inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[12px] font-semibold" data-testid="group-approve-all">
              <Check size={13} /> Approve all
            </button>
            <button onClick={() => onDecideAll("reject")} className="h-8 px-3 inline-flex items-center gap-1 border border-slate-200 hover:border-red-300 hover:text-red-600 text-slate-600 rounded-lg text-[12px] font-semibold" data-testid="group-reject-all">
              <X size={13} /> Reject all
            </button>
          </div>
        )}
      </div>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-100 bg-slate-50/50 space-y-2.5">
          {items === null ? (
            <div className="py-6 grid place-items-center text-slate-400"><Loader2 className="animate-spin" size={18} /></div>
          ) : items.length === 0 ? (
            <div className="py-4 text-center text-[12.5px] text-slate-400">Nothing left in this group.</div>
          ) : (
            <>
              {items.map((t, i) => (
                <ReviewCard key={t.id} task={t} onReview={onReview} onDeleteTask={onDeleteTask}
                  onRemoved={(id) => { setItems((cur) => cur.filter((x) => x.id !== id)); onItemRemoved(); }}
                  isOpportunityOrEnquiry={t.category === "opportunity" || t.category === "new_enquiry"}
                  isSelfSent={t.is_self_sent === true} isSelected={false} onToggleSelect={() => {}} delay={i * 20}
                  hideRecipient={by === "chat"} />
              ))}
              {g.count > items.length && items.length >= 500 && (
                <div className="text-[12px] text-slate-400 text-center">Showing the newest 500 — "Approve all / Reject all" covers all {g.count}.</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function extractPhoneFromChatId(chatId) {
  if (!chatId) return null;
  if (chatId.includes("@g.us")) return null;
  // Only @s.whatsapp.net JIDs encode a real phone number. @lid is a server-side
  // privacy ID — its numeric prefix is NOT a phone and must not be shown as one.
  if (!chatId.endsWith("@s.whatsapp.net")) return null;
  const match = chatId.match(/^(\+?\d+)@/);
  return match ? match[1] : null;
}

function SelfSentConversationGroup({ group, onReview, onDecideMultiple, onDeleteTask, onRemoved, selected, onToggleSelect }) {
  const confirm = useConfirm();
  const { chat_id, chat_name, chat_type, tasks } = group;
  // Prefer the bridge-supplied chat_phone (only set for real phone JIDs);
  // fall back to extracting from chat_id for legacy data.
  const phone = chat_type !== "group"
    ? (tasks[0]?.chat_phone || extractPhoneFromChatId(chat_id))
    : null;
  const isSelfNote = tasks[0]?.chat_name === tasks[0]?.sender_name;
  const taskIds = tasks.map((t) => t.id);

  return (
    <div className="mb-5">
      {/* Conversation thread header */}
      <div className="flex items-center gap-2.5 mb-2 px-1">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {isSelfNote ? (
            <span className="text-[12px] font-bold text-violet-700 flex items-center gap-1.5">
              <PenLine size={13} className="text-violet-500" /> Self-Note
            </span>
          ) : chat_type === "group" ? (
            <>
              <Users size={14} className="text-violet-500 shrink-0" />
              <span className="text-[13px] font-bold text-slate-800 truncate">Sent to group: {chat_name}</span>
            </>
          ) : (
            <>
              <MessageSquare size={14} className="text-violet-500 shrink-0" />
              <span className="text-[13px] font-bold text-slate-800 truncate">Sent to: {chat_name}</span>
              {phone && <span className="text-[11px] text-slate-400 font-mono shrink-0">{phone}</span>}
            </>
          )}
          <span className="text-[11px] text-slate-400 shrink-0">
            {tasks.length} message{tasks.length !== 1 ? "s" : ""}
          </span>
        </div>
        {tasks.length > 1 && (
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => onDecideMultiple(taskIds, "approve", {})}
              className="h-7 px-3 inline-flex items-center gap-1 bg-violet-600 hover:bg-violet-700 text-white rounded-md text-[11px] font-semibold transition active:scale-95"
            >
              <Check size={11} /> Approve All
            </button>
            <button
              onClick={async () => {
                const ok = await confirm({
                  title: `Skip all ${tasks.length} message${tasks.length > 1 ? "s" : ""}?`,
                  message: "They'll be removed from the review queue.",
                  confirmLabel: "Skip all",
                  tone: "danger",
                });
                if (ok) taskIds.forEach(async (id) => { if (await onDeleteTask(id)) onRemoved(id); });
              }}
              className="h-7 px-3 inline-flex items-center gap-1 border border-slate-200 hover:border-red-300 hover:text-red-600 rounded-md text-[11px] font-medium transition"
            >
              <X size={11} /> Skip All
            </button>
          </div>
        )}
      </div>
      {/* Individual task cards indented under the conversation header */}
      <div className="space-y-2 pl-4 border-l-2 border-violet-200">
        {tasks.map((t, i) => (
          <ReviewCard
            key={t.id}
            task={t}
            onReview={onReview}
            onDeleteTask={onDeleteTask}
            onRemoved={onRemoved}
            isOpportunityOrEnquiry={false}
            isSelfSent={true}
            isSelected={selected.has(t.id)}
            onToggleSelect={() => onToggleSelect(t.id)}
            delay={i * 30}
            hideRecipient={true}
          />
        ))}
      </div>
    </div>
  );
}

// Matches the .sw-exit-* + .sw-collapse timings in index.css.
const EXIT_MS = 420;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

function ReviewCard({ task, onReview, onDeleteTask, onRemoved, isOpportunityOrEnquiry, isSelfSent, isSelected, onToggleSelect, delay, hideRecipient = false }) {
  const [editMode, setEditMode] = useState(false);
  const [settingDeadline, setSettingDeadline] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [exiting, setExiting] = useState(null); // null | "approve" | "delete"
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState({
    title: task.title,
    category: task.category,
    priority: task.priority,
    deadline: toLocalInputValue(task.deadline),
    assignee: task.assignee || "",
    notes: task.notes || "",
  });

  // Play the exit animation while the request is in flight; drop the card
  // once both finish, or snap it back if the request failed.
  const runExit = async (kind, request) => {
    setConfirmingDelete(false);
    setExiting(kind);
    const [ok] = await Promise.all([request(), wait(EXIT_MS)]);
    if (ok) onRemoved(task.id);
    else setExiting(null);
  };

  const approve = (edits = {}) => runExit("approve", () => onReview(task.id, "approve", edits));
  const confirmDelete = () => runExit("delete", () => onDeleteTask(task.id));

  const approveEdited = () =>
    runExit("approve", () =>
      onReview(task.id, "edit_approve", {
        title: draft.title,
        category: draft.category,
        priority: draft.priority,
        deadline: draft.deadline ? new Date(draft.deadline).toISOString() : null,
        assignee: draft.assignee || null,
        notes: draft.notes,
      })
    );

  const handleApproveWithDeadline = (deadline, opts) => {
    const edits = { deadline: deadline ? new Date(deadline).toISOString() : null };
    if (deadline && opts?.sendInvite !== undefined) edits.send_calendar_invite = opts.sendInvite;
    setSettingDeadline(false);
    approve(edits);
  };

  const confidenceColor = () => {
    const conf = task.confidence || 0;
    if (conf >= 0.85) return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (conf >= 0.7) return "bg-amber-50 text-amber-700 border-amber-200";
    return "bg-red-50 text-red-700 border-red-200";
  };

  const borderClass = isSelected
    ? `border-2 ${isSelfSent ? "border-violet-500" : "border-blue-500"}`
    : `border-l-4 border border-slate-200 ${
        isSelfSent ? "border-l-violet-500" :
        task.confidence >= 0.85 ? "border-l-blue-600" :
        task.confidence >= 0.7 ? "border-l-yellow-500" : "border-l-red-500"
      }`;

  const leftTint =
    isSelfSent ? "bg-violet-500/[0.04]" :
    task.confidence >= 0.85 ? "bg-blue-600/[0.04]" :
    task.confidence >= 0.7 ? "bg-yellow-500/[0.05]" : "bg-red-500/[0.04]";

  const recipientPhone = task.chat_phone || extractPhoneFromChatId(task.chat_id);
  const hasGrouped = isSelfSent && task.grouped_messages?.length > 0;
  const isLong = hasGrouped || (task.original_text || "").length > 180 || (task.original_text || "").split("\n").length > 3;
  const rejectLabel = isSelfSent ? "No, Skip" : isOpportunityOrEnquiry ? "Delete" : "Reject";
  const pill = "text-[10px] tracking-[0.12em] uppercase font-bold px-1.5 py-0.5 border rounded";

  return (
    <>
    <div className={`sw-collapse ${exiting ? "is-collapsed" : ""}`}>
    <div>
    <article
      className={`relative bg-white rounded-xl grid md:grid-cols-2 overflow-hidden transition-shadow hover:shadow-md ${borderClass} ${
        exiting === "approve" ? "sw-exit-approve pointer-events-none" :
        exiting === "delete" ? "sw-exit-delete pointer-events-none" : "sw-fade-up"
      }`}
      style={exiting ? undefined : { animationDelay: `${delay}ms` }}
      data-testid={`review-card-${task.id}`}
    >
      {exiting && (
        <div className={`absolute inset-0 z-10 grid place-items-center sw-fade-in ${exiting === "approve" ? "bg-emerald-50/80" : "bg-red-50/80"}`}>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-semibold text-white shadow-sm sw-pop ${exiting === "approve" ? "bg-emerald-600" : "bg-red-600"}`}>
            {exiting === "approve" ? <><Check size={14} /> Task created</> : <><Trash2 size={13} /> Deleted</>}
          </span>
        </div>
      )}

      {/* Original message side */}
      <div className={`p-3.5 border-b md:border-b-0 md:border-r border-slate-100 ${leftTint}`}>
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={(e) => { e.preventDefault(); onToggleSelect(); }}
            className="text-slate-400 hover:text-slate-900 transition shrink-0"
            title={isSelected ? "Deselect" : "Select"}
            data-testid={`select-${task.id}`}
          >
            {isSelected ? (
              <CheckSquare size={16} className={isSelfSent ? "text-violet-600" : "text-blue-600"} />
            ) : (
              <Square size={16} />
            )}
          </button>
          {isSelfSent ? (
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-violet-700 flex items-center gap-1">
              <PenLine size={11} /> You sent this
            </span>
          ) : (
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">WhatsApp</span>
          )}
          <span className={pill + " " + (isSelfSent ? "border-violet-200 bg-violet-50 text-violet-700" : chatTypeClasses(task.chat_type || "individual"))}>
            {isSelfSent ? "Self" : task.chat_type === "group" ? "Group" : "Direct"}
          </span>
          <span className="ml-auto text-[11px] text-slate-400 font-mono shrink-0">
            {formatRelative(task.message_received_at || task.created_at)}
          </span>
        </div>

        {/* Who — one line */}
        <div className="mt-1.5 text-[12.5px] text-slate-600 truncate">
          {isSelfSent ? (
            hideRecipient ? null : task.chat_name && task.chat_name !== task.sender_name ? (
              <>
                To <span className="font-semibold text-slate-900">{task.chat_name}</span>
                {task.chat_type === "group" ? (
                  <span className="text-violet-600"> · group</span>
                ) : recipientPhone ? (
                  <span className="text-slate-400 font-mono"> · {recipientPhone}</span>
                ) : null}
              </>
            ) : (
              <span className="text-violet-700 font-medium">Note to yourself</span>
            )
          ) : task.chat_type === "individual" ? (
            <>
              <span className="font-semibold text-slate-900">{task.sender_name}</span>
              {task.sender_phone && <span className="text-slate-400 font-mono"> · {task.sender_phone}</span>}
            </>
          ) : (
            <>
              <span className="font-semibold text-slate-900">{task.chat_name}</span>
              <span className="text-slate-400"> · from </span>
              <span className="font-medium text-slate-800">{task.sender_name}</span>
            </>
          )}
        </div>

        <div
          className={`mt-2 text-[13px] leading-snug text-slate-700 whitespace-pre-wrap px-2.5 py-2 border rounded-md ${
            isSelfSent ? "bg-violet-50/50 border-violet-100" : "bg-white/70 border-slate-200"
          } ${isLong && !expanded ? "line-clamp-3" : ""}`}
        >
          {hasGrouped && expanded ? (
            <div className="space-y-1.5">
              <div className="p-1.5 bg-white border border-violet-100 rounded">{task.original_text}</div>
              {task.grouped_messages.map((m, i) => (
                <div key={i} className="p-1.5 bg-white border border-violet-100 rounded">{m.text}</div>
              ))}
            </div>
          ) : (
            task.original_text
          )}
        </div>
        {isLong && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="mt-1 text-[11.5px] font-medium text-blue-600 hover:text-blue-800"
          >
            {expanded ? "Show less" : hasGrouped ? `Show all ${task.grouped_messages.length + 1} messages` : "Show more"}
          </button>
        )}
      </div>

      {/* AI suggestion / edit side */}
      <div className="p-3.5 flex flex-col">
        <div className="flex flex-wrap items-center gap-1.5">
          {!isSelfSent && (
            <span className={`inline-flex items-center gap-1 ${pill} ${confidenceColor()}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                task.confidence >= 0.85 ? "bg-emerald-500" :
                task.confidence >= 0.7 ? "bg-yellow-500" : "bg-red-500"
              }`} />
              {Math.round((task.confidence || 0) * 100)}%
            </span>
          )}
          <span className={pill} style={{ borderColor: categoryColor(task.category), color: categoryColor(task.category) }}>
            {categoryLabel(task.category)}
          </span>
          <span className={pill + " " + priorityClasses(task.priority)}>{task.priority}</span>
          {task.deadline && (
            <span className={pill + " border-amber-200 bg-amber-50 text-amber-800 font-mono"}>
              Due {formatDateTime(task.deadline)}
            </span>
          )}
          {isOpportunityOrEnquiry && !confirmingDelete && (
            <button
              onClick={() => setConfirmingDelete(true)}
              className="ml-auto h-6 w-6 grid place-items-center text-slate-400 hover:text-red-600 rounded"
              title="Delete"
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>

        {!editMode ? (
          <>
            <div className="text-[15px] font-semibold text-slate-900 leading-snug mt-2">{task.title}</div>
            {task.summary && (
              <p className="text-[12.5px] text-slate-500 italic mt-0.5 line-clamp-2">"{task.summary}"</p>
            )}
            {task.notes && (
              <p className="text-[12px] text-slate-600 mt-1 line-clamp-2">
                <span className="font-semibold text-slate-500">Notes: </span>{task.notes}
              </p>
            )}
          </>
        ) : (
          <div className="space-y-2 mt-2">
            <input
              className="w-full h-8 px-2 border border-slate-200 rounded text-[13px] focus:border-slate-900 outline-none"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              data-testid={`review-edit-title-${task.id}`}
            />
            <div className="grid grid-cols-2 gap-2">
              <select
                className="h-8 px-2 border border-slate-200 rounded text-[12.5px] bg-white focus:border-slate-900 outline-none"
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                data-testid={`review-edit-cat-${task.id}`}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
              <select
                className="h-8 px-2 border border-slate-200 rounded text-[12.5px] bg-white focus:border-slate-900 outline-none"
                value={draft.priority}
                onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
                data-testid={`review-edit-pri-${task.id}`}
              >
                {PRIORITIES.map((p) => (
                  <option key={p.key} value={p.key}>{p.label}</option>
                ))}
              </select>
              <input
                type="datetime-local"
                className="h-8 px-2 border border-slate-200 rounded text-[12.5px] focus:border-slate-900 outline-none"
                value={draft.deadline}
                onChange={(e) => setDraft({ ...draft, deadline: e.target.value })}
                min={new Date().toISOString().slice(0, 16)}
                data-testid={`review-edit-deadline-${task.id}`}
              />
              <input
                placeholder="Assignee"
                className="h-8 px-2 border border-slate-200 rounded text-[12.5px] focus:border-slate-900 outline-none"
                value={draft.assignee}
                onChange={(e) => setDraft({ ...draft, assignee: e.target.value })}
                data-testid={`review-edit-assignee-${task.id}`}
              />
              <div className="col-span-2 text-[11px] text-slate-400 -mt-1">No date? Brainydo sets one from the priority when you approve.</div>
            </div>
            <textarea
              placeholder="Notes (optional)"
              rows={2}
              className="w-full p-2 border border-slate-200 rounded text-[12.5px] focus:border-slate-900 outline-none"
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              data-testid={`review-edit-notes-${task.id}`}
            />
          </div>
        )}

        <div className="mt-auto pt-3 flex gap-2">
          {confirmingDelete ? (
            <div
              className="flex-1 flex items-center gap-2 sw-pop"
              onKeyDown={(e) => e.key === "Escape" && setConfirmingDelete(false)}
            >
              <span className="mr-auto text-[12.5px] font-semibold text-red-700 flex items-center gap-1.5">
                <Trash2 size={13} /> Delete this?
              </span>
              <button
                onClick={() => setConfirmingDelete(false)}
                className="h-9 px-3 border border-slate-200 hover:border-slate-400 rounded-lg text-[12.5px] font-medium transition"
                data-testid={`review-cancel-delete-${task.id}`}
              >
                Cancel
              </button>
              <button
                autoFocus
                onClick={confirmDelete}
                className="h-9 px-4 inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-[12.5px] font-semibold transition active:scale-95"
                data-testid={`review-confirm-delete-${task.id}`}
              >
                <Trash2 size={13} /> Delete
              </button>
            </div>
          ) : !editMode ? (
            <>
              <button
                onClick={() => task.deadline ? approve() : setSettingDeadline(true)}
                className={`flex-1 h-9 px-3 inline-flex items-center justify-center gap-1.5 text-white rounded-lg text-[12.5px] font-semibold transition active:scale-95 ${isSelfSent ? "bg-violet-600 hover:bg-violet-700" : "bg-blue-600 hover:bg-blue-700"}`}
                data-testid={`review-approve-${task.id}`}
              >
                <Check size={14} /> {isSelfSent ? "Yes, Create Task" : `Approve${task.deadline ? "" : " & Set Deadline"}`}
              </button>
              <button
                onClick={() => setEditMode(true)}
                className="h-9 px-3 inline-flex items-center gap-1.5 border border-slate-200 hover:border-blue-400 rounded-lg text-[12.5px] font-medium transition"
                data-testid={`review-edit-${task.id}`}
              >
                Edit
              </button>
              <button
                onClick={() => setConfirmingDelete(true)}
                className="h-9 px-3 inline-flex items-center gap-1.5 border border-slate-200 hover:border-red-400 hover:text-red-600 rounded-lg text-[12.5px] font-medium transition"
                data-testid={`review-reject-${task.id}`}
              >
                <X size={14} /> {rejectLabel}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={approveEdited}
                className="flex-1 h-9 px-3 inline-flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[12.5px] font-semibold transition active:scale-95"
                data-testid={`review-save-approve-${task.id}`}
              >
                <Check size={14} /> Save & approve
              </button>
              <button
                onClick={() => setEditMode(false)}
                className="h-9 px-3 border border-slate-200 hover:border-slate-400 rounded-lg text-[12.5px] font-medium transition"
                data-testid={`review-cancel-edit-${task.id}`}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </article>
    </div>
    </div>

    {settingDeadline && (
      <DeadlinePicker
        taskId={task?.id || null}
        onSelect={handleApproveWithDeadline}
        onCancel={() => setSettingDeadline(false)}
        currentDeadline={task?.deadline || null}
      />
    )}
    </>
  );
}
